import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileText, Upload, Trash2, Download, Loader2, Plus, Search, FolderOpen, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole } from "@/lib/roles";
import { plural } from "@/lib/plural";
import MissingDocumentsPanel from "@/components/MissingDocumentsPanel";

const CATEGORY_ICONS = {
  Report: "📊",
  Certificato: "🏅",
  "Action Plan": "🎯",
  Brochure: "📘",
  Altro: "📎",
};
const CATEGORIES = ["Report", "Certificato", "Action Plan", "Brochure", "Altro"];

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("ALL");
  const [filterOrg, setFilterOrg] = useState("ALL");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "Report",
    organization_id: "", file: null,
  });
  const [uploading, setUploading] = useState(false);
  const canWrite = hasRole(user, "admin", "valuatore");
  const isCliente = hasRole(user, "cliente");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = !isCliente && filterOrg !== "ALL"
        ? { organization_id: filterOrg } : {};
      const { data } = await api.get("/library/documents", { params });
      setDocs(data);
      if (canWrite) {
        const { data: o } = await api.get("/organizations");
        setOrgs(o);
      }
    } finally { setLoading(false); }
  }, [filterOrg, isCliente, canWrite]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => docs.filter((d) => {
      if (filterCat !== "ALL" && d.category !== filterCat) return false;
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (d.title || "").toLowerCase().includes(q)
        || (d.description || "").toLowerCase().includes(q)
        || (d.filename || "").toLowerCase().includes(q)
        || (d.organization_name || "").toLowerCase().includes(q)
      );
    }),
    [docs, filterCat, search]
  );

  const grouped = useMemo(() => CATEGORIES.reduce((acc, c) => {
    acc[c] = filtered.filter((d) => d.category === c);
    return acc;
  }, {}), [filtered]);

  async function handleUpload(e) {
    e.preventDefault();
    if (!form.file) { toast.error("Seleziona un file"); return; }
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!form.organization_id) { toast.error("Seleziona l'azienda destinataria"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("category", form.category);
      fd.append("organization_id", form.organization_id);
      fd.append("file", form.file);
      await api.post("/library/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato e disponibile per il cliente");
      setOpen(false);
      setForm({ title: "", description: "", category: "Report", organization_id: "", file: null });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload");
    } finally { setUploading(false); }
  }

  async function remove(d) {
    if (!confirm(`Eliminare "${d.title}"?`)) return;
    try {
      await api.delete(`/library/documents/${d.id}`);
      toast.success("Documento eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  function downloadUrl(d) {
    return `${API}/library/documents/${d.id}/download`;
  }

  function formatSize(n) {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;
  }

  const titlePart = isCliente ? "I tuoi documenti VIVA" : "Documenti per cliente";
  const subtitlePart = isCliente
    ? "Report, certificato e altri output predisposti per la tua azienda da VIVA ESG."
    : "Repository per-azienda: ogni cliente vede solo i propri deliverable.";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2 flex items-center gap-2">
            <FolderOpen className="w-3.5 h-3.5" /> Repository documentale
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{titlePart}</h1>
          <p className="text-slate-500 mt-1">{subtitlePart}</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-doc-button" className="h-11 px-5 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Carica documento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Carica deliverable per cliente</DialogTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Il documento sarà visibile esclusivamente all&apos;azienda destinataria.
                </p>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Azienda destinataria *</Label>
                  <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                    <SelectTrigger data-testid="doc-org-select" className="mt-1.5 h-10 rounded-lg">
                      <SelectValue placeholder="Seleziona azienda..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {orgs.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.ragione_sociale}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Titolo *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Es. Report annuale ESG 2026"
                    data-testid="doc-title-input"
                    className="mt-1.5 h-10 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Categoria *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="doc-category-select" className="mt-1.5 h-10 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{CATEGORY_ICONS[c]} {c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descrizione</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Breve descrizione del contenuto"
                    rows={3}
                    data-testid="doc-desc-input"
                    className="mt-1.5 rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">File *</Label>
                  <Input
                    type="file"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    data-testid="doc-file-input"
                    className="mt-1.5 h-10 rounded-lg cursor-pointer"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">PDF, DOC, XLS, PPT, PNG, JPG, ZIP · max 25 MB</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" disabled={uploading} data-testid="doc-upload-submit" className="bg-viva-500 hover:bg-viva-600 text-white">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Carica
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {hasRole(user, "admin") && <MissingDocumentsPanel onRegenerated={load} />}

      {/* Filter row */}
      <Card className="p-4 rounded-2xl border-slate-100 shadow-soft mb-6 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={filterCat === "ALL" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterCat("ALL")}
            data-testid="filter-cat-ALL"
            className={filterCat === "ALL" ? "bg-viva-500 hover:bg-viva-600 text-white" : ""}
          >
            Tutti <span className="ml-1.5 opacity-70">({docs.length})</span>
          </Button>
          {CATEGORIES.map((c) => {
            const n = docs.filter((d) => d.category === c).length;
            return (
              <Button
                key={c}
                variant={filterCat === c ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterCat(c)}
                data-testid={`filter-cat-${c}`}
                className={filterCat === c ? "bg-viva-500 hover:bg-viva-600 text-white" : ""}
              >
                <span className="mr-1">{CATEGORY_ICONS[c]}</span> {c} <span className="ml-1.5 opacity-70">({n})</span>
              </Button>
            );
          })}
        </div>
        {canWrite && orgs.length > 1 && (
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger data-testid="filter-org-select" className="h-9 w-56 rounded-lg ml-2">
              <Building2 className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="ALL">Tutte le aziende</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.ragione_sociale}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per titolo, descrizione, azienda…"
            data-testid="doc-search-input"
            className="pl-9 h-9 w-80 rounded-lg"
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-12 rounded-2xl border-slate-100 shadow-soft text-center text-slate-500" data-testid="documents-empty">
          <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.25} />
          <p className="font-medium text-slate-700">
            {isCliente
              ? "Nessun documento ancora disponibile"
              : "Nessun documento" + (search ? " trovato" : " caricato")}
          </p>
          {isCliente && (
            <p className="text-xs text-slate-400 mt-1">
              Quando VIVA ESG avrà elaborato il report e il certificato della tua azienda, li troverai qui.
            </p>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {(filterCat === "ALL" ? CATEGORIES : [filterCat]).map((cat) => {
            const items = grouped[cat] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat} data-testid={`doc-group-${cat}`}>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                  <span>{CATEGORY_ICONS[cat]}</span> {cat}
                  <span className="text-slate-400 normal-case">· {plural(items.length, "documento", "documenti")}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((d) => (
                    <Card
                      key={d.id}
                      data-testid={`doc-card-${d.id}`}
                      className="p-5 rounded-2xl border-slate-100 shadow-soft shadow-hover flex flex-col"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-viva-50 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-viva-600" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-semibold text-slate-900 truncate">{d.title}</div>
                          <div className="text-[11px] text-slate-400 truncate">{d.filename} · {formatSize(d.size)}</div>
                        </div>
                      </div>
                      {!isCliente && d.organization_name && (
                        <div className="text-[11px] uppercase tracking-wider font-bold text-viva-700 mb-2 flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {d.organization_name}
                        </div>
                      )}
                      {d.description && (
                        <p className="text-xs text-slate-600 leading-relaxed mb-3 line-clamp-3">{d.description}</p>
                      )}
                      <div className="text-[11px] text-slate-400 mb-3">
                        Caricato da {d.uploaded_by_name || "—"} ·{" "}
                        {new Date(d.uploaded_at).toLocaleDateString("it-IT")}
                        {d.document_date && (
                          <span data-testid={`doc-issue-date-${d.id}`}> · Data emissione {new Date(d.document_date).toLocaleDateString("it-IT")}</span>
                        )}
                      </div>
                      <div className="mt-auto flex items-center gap-2">
                        <a
                          href={downloadUrl(d)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`doc-download-${d.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-viva-200 text-viva-700 hover:bg-viva-50 text-sm font-medium transition"
                        >
                          <Download className="w-3.5 h-3.5" /> Scarica
                        </a>
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(d)}
                            data-testid={`doc-delete-${d.id}`}
                            className="h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
